import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";

export type EventRow = Tables<"events">;
export type TicketTypeRow = Tables<"ticket_types">;

/** All published events across the platform (what Groovers and Creators browse). */
export function usePublishedEvents(limit = 60) {
  return useQuery({
    queryKey: ["events", "published", limit],
    refetchInterval: 30_000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("status", "published")
        .order("event_date", { ascending: true })
        .limit(limit);
      if (error) throw error;
      return data as EventRow[];
    },
  });
}

/** Events owned by the signed-in venue/creator (drafts included). */
export function useMyEvents(ownerId: string | null) {
  return useQuery({
    queryKey: ["events", "mine", ownerId],
    enabled: !!ownerId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("events")
        .select("*")
        .eq("owner_id", ownerId!)
        .order("event_date", { ascending: false });
      if (error) throw error;
      return data as EventRow[];
    },
  });
}

export function useEvent(eventId: string | null) {
  return useQuery({
    queryKey: ["events", "one", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase.from("events").select("*").eq("id", eventId!).maybeSingle();
      if (error) throw error;
      return data as EventRow | null;
    },
  });
}

export function useTicketTypes(eventId: string | null) {
  return useQuery({
    queryKey: ["ticket_types", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("ticket_types")
        .select("*")
        .eq("event_id", eventId!)
        .order("price", { ascending: true });
      if (error) throw error;
      return data as TicketTypeRow[];
    },
  });
}

// Event and ticket saves go through a verified owner-only function so they stay free for everyone.
async function eventWrite(op: string, data: unknown, id?: string) {
  const { data: out, error } = await supabase.functions.invoke("event-write", { body: { op, data, id } });
  if (error) {
    let msg = error.message;
    try { const ctx = (error as { context?: Response }).context; if (ctx) msg = (await ctx.json()).error ?? msg; } catch { /* keep message */ }
    throw new Error(msg);
  }
  if (out?.error) throw new Error(out.error);
  return out;
}

export function useSaveEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TablesInsert<"events">) => {
      return (await eventWrite("create_event", payload)) as EventRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useUpdateEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: TablesUpdate<"events"> & { id: string }) => {
      return (await eventWrite("update_event", patch, id)) as EventRow;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useDeleteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await eventWrite("delete_event", undefined, id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["events"] }),
  });
}

export function useCreateTicketType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: TablesInsert<"ticket_types">) => {
      return (await eventWrite("create_ticket_type", payload)) as TicketTypeRow;
    },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: ["ticket_types", row.event_id] }),
  });
}

/** Deletes a tier; tiers that already sold tickets are taken off sale instead. */
export function useDeleteTicketType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await eventWrite("delete_ticket_type", undefined, id)) as { id: string; event_id: string; archived: boolean },
    onSuccess: (row) => qc.invalidateQueries({ queryKey: ["ticket_types", row.event_id] }),
  });
}

/* ---------------- Door team & shared scanning ---------------- */

export type EventStaffRow = Tables<"event_staff">;

/** Events the signed-in user may scan: their own, ones hosted at their venue, or ones they were added to. */
export function useScannableEvents(userId: string | null) {
  return useQuery({
    queryKey: ["events", "scannable", userId],
    enabled: !!userId,
    queryFn: async () => {
      const { data: staff, error: staffError } = await supabase
        .from("event_staff")
        .select("event_id")
        .eq("user_id", userId!)
        .eq("can_scan", true);
      if (staffError) throw staffError;
      const staffIds = (staff ?? []).map((s) => s.event_id);

      const filters = [`owner_id.eq.${userId}`, `venue_id.eq.${userId}`];
      if (staffIds.length) filters.push(`id.in.(${staffIds.join(",")})`);

      const { data, error } = await supabase
        .from("events")
        .select("*")
        .or(filters.join(","))
        .order("event_date", { ascending: false });
      if (error) throw error;
      return data as EventRow[];
    },
  });
}

/** The door team for an event (visible to the owner, the host venue and the team members). */
export function useEventStaff(eventId: string | null) {
  return useQuery({
    queryKey: ["event_staff", eventId],
    enabled: !!eventId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("event_staff")
        .select("*")
        .eq("event_id", eventId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return data as EventStaffRow[];
    },
  });
}

export function useAddEventStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ eventId, username, addedBy }: { eventId: string; username: string; addedBy: string }) => {
      const handle = username.trim().replace(/^@/, "");
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id, username")
        .ilike("username", handle)
        .maybeSingle();
      if (profileError) throw profileError;
      if (!profile) throw new Error("No user with that username");
      const { error } = await supabase
        .from("event_staff")
        .upsert({ event_id: eventId, user_id: profile.id, can_scan: true, added_by: addedBy }, { onConflict: "event_id,user_id" });
      if (error) throw error;
      return profile.id;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["event_staff"] }),
  });
}

export function useSetEventStaffScan() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, canScan }: { id: string; canScan: boolean }) => {
      const { error } = await supabase.from("event_staff").update({ can_scan: canScan }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["event_staff"] }),
  });
}

export function useRemoveEventStaff() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("event_staff").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["event_staff"] }),
  });
}

/** Venues the creator has a confirmed booking with — they can host their own ticketed event there. */
export function useBookedVenues(creatorId: string | null) {
  return useQuery({
    queryKey: ["bookings", "venues", creatorId],
    enabled: !!creatorId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("bookings")
        .select("venue_id, event_date, status")
        .eq("creator_id", creatorId!)
        .in("status", ["confirmed", "accepted", "paid"]);
      if (error) throw error;
      const ids = Array.from(new Set((data ?? []).map((b) => b.venue_id)));
      if (!ids.length) return [] as { id: string; name: string; city: string | null }[];
      const { data: profiles, error: profileError } = await supabase
        .from("profiles")
        .select("id, full_name, username, business_name, city")
        .in("id", ids);
      if (profileError) throw profileError;
      return (profiles ?? []).map((p) => ({
        id: p.id,
        name: p.business_name || p.full_name || p.username,
        city: p.city,
      }));
    },
  });
}
